import { Checkbox, Divider, message } from "antd"
import { useEffect, useState } from "react"
import { useNavigate, useParams  } from "react-router-dom"
import Head from "../../components/Head"
import Button from "../../components/UI/Button"
import Input from "../../components/UI/Input"
import SelectInput from "../../components/UI/SelectInput"
import { notificationError } from "../../services/notification"
import { createOrUpdateRole, getRoleById } from '../../services/roles'
import translate, { legislationBase } from "../../services/translate"
import { onlySpaces } from "../../utils/formValidation"


function NewRole() {

  const history = useNavigate()
  let { id } = useParams();
  const selectOptions = [
    {name: 'Investigator', value: 'investigator'},
    {name: 'Manufacturer', value: 'manufacturer'},
    {name: 'Distributor', value: 'distributor'},
    {name: 'Charity', value: 'organization'},
    {name: 'Location', value: 'location'},
  ]
  
  const [disabledOptions, setDisabledOptions] = useState(false)
  const [form, setForm] = useState({
    roleName: '',
    affiliation: selectOptions[0].value,
    permissions: {}
  })
  const [orig, setOrig] = useState({
    roleName: '',
    affiliation: selectOptions[0].value,
    permissions: {}
  })
  const [filter, setFilter] = useState('')
  const [permissionsOptions, setPermissionsOptions] = useState([])


  useEffect(() => {  
    const permissions = Object.entries(legislationBase.permissionObject).map(entry => {
      return {name: entry[1], value: entry[0]};
    });
    setPermissionsOptions(permissions as any)
    if(id) {
      (async () => {
        const result: any = await getRoleById({id})

        if(!result || !result.id) return notificationError(`Can't find a role with id ${id}`);

        let permissions = {}

        result.permissions.forEach((item: any) => {
          if(!item.permissionObject || !item.accessLevel) return
          permissions = {
            ...permissions,
            [item.permissionObject]: item.accessLevel
          }
          if(item.permissionObject === 'all') {
            setDisabledOptions(true)
          }
        })
        setForm({
          roleName: result.roleName,
          affiliation: result.affiliation,
          permissions
        })
        setOrig({
          roleName: result.roleName,
          affiliation: result.affiliation,
          permissions: Object.assign({},permissions)
        })
      })()
    }
  },[])

  const isChanged = () => {

    if(!form.roleName
        || !form.affiliation
        || !form.permissions
    ) return false
    if(form.affiliation!==orig.affiliation) return true
    if(JSON.stringify(form.permissions)!==JSON.stringify(orig.permissions)) return true
    return form.roleName !== orig.roleName;

  }
  const onFormChange = (val: string, name: string) => {
    setForm({
      ...form,
      [name]: val
    })
  }

  const onPermissionChange = (e: any, obj: string) => {
    let permissions = form.permissions
    
    if((!e || !e[0]) && (obj && permissions[obj as keyof typeof permissions])) {
       delete permissions[obj as keyof typeof permissions]
       setDisabledOptions(false)
    }

    if(obj === 'all' && (e[0] === 'read' || e[0] === 'manage') ) {
      permissions = {
        [obj]: e[0]
      }
      setDisabledOptions(true)
    } else if(e && e[0]) {
      permissions = {
        ...permissions,
        [obj]: e[0]
      }
      setDisabledOptions(false)
    }

    if(!permissions) return
    setForm({
      ...form,
      permissions
    })
  }

  const onSubmit = async () => {
    if(!form.permissions || Object.keys(form.permissions).length === 0) return notificationError('Enter at least one permission')
    if(!form.roleName || onlySpaces(form.roleName)) return notificationError('Enter a role name')
    if(!form.affiliation || onlySpaces(form.affiliation) ) return notificationError('Enter a role affiliation')

    const permissions = Object.entries(form.permissions).map(entry => {
      return {permissionObject: entry[0], accessLevel: entry[1]};
    });
    const payload = {
      ...form,
      permissions
    }
    if(id) await createOrUpdateRole({id,...payload})
    if(!id) await createOrUpdateRole(payload)

    history(-1)
  }

  const filteredPermissions = () => {
    const filtered = permissionsOptions.filter((permission: any) => permission.name.toLowerCase().includes(filter.toLowerCase()))
    if(filtered.length === 0) return permissionsOptions
    return filtered
  }

  return (
    <div className="new-role">
      <Divider orientation="left" plain>
        <strong>Role Info</strong>
      </Divider>
      <div className="new-role__inputs">
        <Input
          label="Role Name (required)"
          value={form.roleName}
          onChange={(e: any) => onFormChange(e.target.value, 'roleName')}
        />
        <SelectInput
          label="Role Affiliation Level (required)"
          value={form.affiliation}
          options={selectOptions}
          onSelectChange={(val) => onFormChange(val?.value ? val.value : val, 'affiliation')}
        />
      </div>
      <Divider orientation="left" plain>
        <strong>Permissions</strong>
      </Divider>
      <div className="new-role__permissions">
        <div className="new-role__permissions__cell">
          <div className="new-role__permissions__cell__title">
          <Input
            label="Permission Filter"
            value={filter}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilter(e.target.value)}
          />
          </div>
        </div>
        {filteredPermissions().map((item: any) => (
          <>
          <div key={item.name} className="new-role__permissions__cell new-role__permissions__cell--divided">
            <div className="new-role__permissions__cell__title">
              {item.name}
            </div>
            <div className="new-role__permissions__cell__view">
              {item.name === 'Devices Push Command' ? (
                <Checkbox.Group disabled={item.value !== 'all' && disabledOptions ? true : false}
                     options={['manage']}
                     value={form.permissions[item.value as keyof typeof form.permissions]}
                     onChange={(e) => onPermissionChange(e, item.value)} />
              ):(
                <Checkbox.Group disabled={item.value !== 'all' && disabledOptions ? true : false}
                     options={['read', 'manage']}
                     value={form.permissions[item.value as keyof typeof form.permissions]}
                     onChange={(e) => onPermissionChange(e, item.value)} />
              )}
            </div>
          </div>
          </>
        ))}
      </div>
      <div>
        <p className="new-role__paragraph">
          * Indicates required fields
        </p>
      </div>
      <div className="new-role__buttons">
        <Button onClick={() => history(-1)} type='secondary'>
          <p id="cancel-button">Cancel</p>
        </Button>
        <Button
            onClick={() => onSubmit()}
            type='primary'
            disabled={!isChanged()}
        >
          <p id="save-button">Save</p>
        </Button>
      </div>
    </div>
  )
}

export default NewRole